import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

function formatNumber(value, digits = 1) {
  const num = Number(value);
  if (Number.isNaN(num)) return "—";
  return num.toFixed(digits);
}

function formatMbpsFromKbps(kbps) {
  const num = Number(kbps);
  if (Number.isNaN(num)) return "—";
  return `${(num / 1000).toFixed(1)} Mbps`;
}

function MetricRow({ label, value }) {
  return (
    <MDTypography variant="caption" color="text" display="block">
      {label}: <strong>{value}</strong>
    </MDTypography>
  );
}

MetricRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

function CpeSectorAnalysisDialog({ open, onClose, cpeName, loading, error, snapshot }) {
  const data = snapshot?.analysis_data || {};
  const analyzedAt = snapshot?.analyzed_at
    ? String(snapshot.analyzed_at).replace("T", " ").slice(0, 19)
    : "—";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Sector Analysis Data — {cpeName || "CPE"}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <MDBox display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </MDBox>
        ) : error ? (
          <MDTypography variant="body2" color="error">
            {error}
          </MDTypography>
        ) : !snapshot ? (
          <MDTypography variant="body2" color="text">
            No sector analysis snapshot is stored for this CPE yet. Run sector analysis on the tower
            that serves this client.
          </MDTypography>
        ) : (
          <MDBox>
            <MDTypography variant="body2" color="text" mb={2}>
              Latest snapshot from sector health analysis (matched by client name, not IP).
            </MDTypography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <MDTypography variant="button" fontWeight="medium" mb={1}>
                  Run context
                </MDTypography>
                <MetricRow label="Analyzed at" value={analyzedAt} />
                <MetricRow label="Sector SSID" value={snapshot.sector_ssid || "—"} />
                <MetricRow label="Sector IP" value={snapshot.sector_ip || "—"} />
                <MetricRow label="Client IP (at analysis)" value={snapshot.client_ip || data.ip || "—"} />
                <MetricRow label="MAC" value={snapshot.mac_address || data.mac || "—"} />
              </Grid>
              <Grid item xs={12} md={6}>
                <MDTypography variant="button" fontWeight="medium" mb={1}>
                  Health summary
                </MDTypography>
                <MetricRow label="Severity" value={snapshot.severity || data.severity || "—"} />
                <MetricRow
                  label="Overall impact"
                  value={
                    snapshot.overall_impact != null
                      ? formatNumber(snapshot.overall_impact, 1)
                      : formatNumber(data?.scores?.overall_impact, 1)
                  }
                />
                <MetricRow label="Signal" value={`${formatNumber(data.signal)} dBm`} />
                <MetricRow label="CINR" value={formatNumber(data.cinr)} />
                <MetricRow label="DL link score" value={formatNumber(data.dl_linkscore, 0)} />
                <MetricRow label="UL link score" value={formatNumber(data.ul_linkscore, 0)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <MDTypography variant="button" fontWeight="medium" mb={1}>
                  Capacity
                </MDTypography>
                <MetricRow label="DL capacity" value={formatMbpsFromKbps(data.dcap)} />
                <MetricRow label="UL capacity" value={formatMbpsFromKbps(data.ucap)} />
                <MetricRow label="DL rate" value={formatMbpsFromKbps(data.dl_rate)} />
                <MetricRow label="UL rate" value={formatMbpsFromKbps(data.ul_rate)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <MDTypography variant="button" fontWeight="medium" mb={1}>
                  Airtime & hardware
                </MDTypography>
                <MetricRow label="Total airtime usage" value={`${formatNumber(data.total_usage)}%`} />
                <MetricRow label="TX use" value={`${formatNumber(data.txuse)}%`} />
                <MetricRow label="RX use" value={`${formatNumber(data.rxuse)}%`} />
                <MetricRow label="Ethernet speed" value={data.eth_speed ? `${data.eth_speed} Mbps` : "—"} />
                <MetricRow label="Distance" value={data.distance != null ? `${formatNumber(data.distance, 0)} m` : "—"} />
              </Grid>
              {(data.root_cause || data.flag_summary) && (
                <Grid item xs={12}>
                  <MDTypography variant="button" fontWeight="medium" mb={1}>
                    Diagnosis
                  </MDTypography>
                  {data.root_cause ? (
                    <MDTypography variant="body2" color="text" mb={1}>
                      Root cause: {data.root_cause}
                    </MDTypography>
                  ) : null}
                  {data.flag_summary ? (
                    <MDTypography variant="body2" color="text">
                      Flags: {data.flag_summary}
                    </MDTypography>
                  ) : null}
                </Grid>
              )}
              {Array.isArray(data.recommendations) && data.recommendations.length > 0 ? (
                <Grid item xs={12}>
                  <MDTypography variant="button" fontWeight="medium" mb={1}>
                    Recommendations
                  </MDTypography>
                  {data.recommendations.map((item) => (
                    <MDTypography key={item} variant="body2" color="text" display="block">
                      • {item}
                    </MDTypography>
                  ))}
                </Grid>
              ) : null}
            </Grid>
          </MDBox>
        )}
      </DialogContent>
      <DialogActions>
        <MDButton onClick={onClose}>Close</MDButton>
      </DialogActions>
    </Dialog>
  );
}

CpeSectorAnalysisDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  cpeName: PropTypes.string,
  loading: PropTypes.bool,
  error: PropTypes.string,
  snapshot: PropTypes.object,
};

export default CpeSectorAnalysisDialog;
